import { useRef, useEffect, useState, FormEvent } from 'react';
import { List, WandSparkles, Lightbulb, TriangleAlert, ChevronDown, ChevronUp } from 'lucide-react';
import { ExamplesDropdown } from './ExamplesDropdown';

interface ShoppingListFormProps {
  value: string;
  onChange: (value: string) => void;
  onSubmit: () => void;
  onClear: () => void;
  isDisabled: boolean;
}

const PLACEHOLDER = 'Escribí lo que necesitás (no hace falta que sea prolijo)';

const TIPS_STORAGE_KEY = 'smart_list_tips_open';

export function ShoppingListForm({ value, onChange, onSubmit, onClear, isDisabled }: ShoppingListFormProps) {
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const [tipsOpen, setTipsOpen] = useState(() => {
    try {
      return localStorage.getItem(TIPS_STORAGE_KEY) !== '0';
    } catch {
      return true;
    }
  });

  useEffect(() => {
    if (textareaRef.current && !isDisabled) {
      textareaRef.current.focus();
    }
  }, []);

  const toggleTips = () => {
    const next = !tipsOpen;
    setTipsOpen(next);
    try { localStorage.setItem(TIPS_STORAGE_KEY, next ? '1' : '0'); } catch {}
  };

  const handleExample = (example: string) => {
    onChange(value ? value + '\n' + example : example);
    textareaRef.current?.focus();
  };

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (value.trim()) onSubmit();
  };

  return (
    <div className="bg-white rounded-lg p-5 md:p-[30px] shadow-sm mb-6 md:mb-[30px]">
      <form onSubmit={handleSubmit}>
        <label htmlFor="shopping_list" className="block text-base font-semibold text-gray-800 mb-2.5">
          <List className="inline w-4 h-4 mr-1 -mt-0.5" />
          ¿Qué necesitás?
        </label>

        <textarea
          ref={textareaRef}
          className="w-full text-base border-2 border-gray-200 rounded-md p-4 resize-y font-[inherit] transition-colors focus:border-green-500 focus:outline-none focus:ring-[3px] focus:ring-green-500/15 disabled:bg-gray-100 disabled:text-gray-400 disabled:cursor-not-allowed disabled:opacity-70"
          id="shopping_list"
          name="shopping_list"
          rows={12}
          placeholder={PLACEHOLDER}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          disabled={isDisabled}
        />

        {/* Collapsible tips */}
        <div className="bg-gray-50 border-l-4 border-blue-500 rounded mt-4 overflow-hidden">
          <button
            type="button"
            className="flex items-center justify-between w-full py-3 px-5 bg-transparent border-0 cursor-pointer text-left"
            onClick={toggleTips}
          >
            <strong className="text-blue-500 flex items-center gap-1">
              <Lightbulb className="w-4 h-4" />
              Consejos
            </strong>
            {tipsOpen
              ? <ChevronUp className="w-4 h-4 text-gray-400" />
              : <ChevronDown className="w-4 h-4 text-gray-400" />
            }
          </button>
          {tipsOpen && (
            <ul className="bg-white mx-5 mb-3.5 py-2.5 px-2.5 pl-8 rounded text-sm leading-[1.8] text-gray-800 mt-0 list-disc">
              <li className="mb-1">Escribilo como lo pienses: <strong>2kg de tomates, medio kilo de cebolla, unas lechugas</strong></li>
              <li className="mb-1">También podés pedir recetas: <strong>&quot;Lo que necesito para una tarta de acelga&quot;</strong></li>
              <li className="mb-1">No hace falta que sea prolijo, nosotros lo interpretamos</li>
              <li className="mb-0">
                <strong>
                  <TriangleAlert className="inline w-3.5 h-3.5 mr-0.5 -mt-0.5 text-amber-500" />
                  Siempre te mostramos el carrito antes de confirmar
                </strong>
              </li>
            </ul>
          )}
        </div>

        <div className="flex gap-2.5 mt-5 max-md:flex-col max-md:w-full">
          <button
            type="submit"
            className="inline-flex items-center justify-center gap-2 bg-green-600 text-white border-none py-3 px-8 text-base font-semibold rounded-md cursor-pointer transition-all hover:enabled:bg-green-700 hover:enabled:-translate-y-px hover:enabled:shadow-lg hover:enabled:shadow-green-600/30 disabled:opacity-60 disabled:cursor-not-allowed disabled:transform-none max-md:w-full"
            disabled={isDisabled || !value.trim()}
          >
            <WandSparkles className="w-4 h-4" />
            <span>Ver carrito sugerido</span>
          </button>

          <ExamplesDropdown onSelect={handleExample} disabled={isDisabled} />
        </div>
      </form>
    </div>
  );
}
